import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import { Settings, User, Building, Bell, Lock, CreditCard, ArrowLeft } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SecuritySettings from './security';
import CompanySettings from './company';
import { TimePreferencesSettings } from '@/components/settings/time-preferences';

export default function SettingsPage() {
  const { currentMode, user } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState(location.pathname.split('/')[2] || 'profile');
  const [customUrl, setCustomUrl] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    messages: true,
    connections: true,
    opportunities: true,
    applications: true,
    interviews: true,
    profile_views: true,
    content_engagement: true,
    company_updates: true,
    team_activity: true,
    product_updates: true,
    marketing_emails: true,
    digest_emails: true,
    email_notifications: true,
    push_notifications: true
  });

  // Fetch notification preferences
  useEffect(() => {
    const fetchNotificationPrefs = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('notification_preferences')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        if (data?.notification_preferences) {
          setNotificationPrefs(data.notification_preferences);
        }
      } catch (error) {
        console.error('Error fetching notification preferences:', error);
      }
    };

    fetchNotificationPrefs();
  }, [user]);

  // Save notification preferences
  const saveNotificationPrefs = async (prefs: Partial<typeof notificationPrefs>) => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      const updatedPrefs = { ...notificationPrefs, ...prefs };
      const { error } = await supabase
        .from('profiles')
        .update({ 
          notification_preferences: updatedPrefs,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setNotificationPrefs(updatedPrefs);
      toast({
        title: 'Success',
        description: 'Notification preferences updated',
      });
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update notification preferences',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    ...(currentMode === 'hire' ? [{ id: 'company', label: 'Company', icon: Building }] : []),
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'billing', label: 'Billing', icon: CreditCard },
  ];

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/dashboard">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
            <p className="text-muted-foreground">
              Manage your account settings and preferences
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-muted p-1 grid grid-cols-5 gap-2">
            {tabs.map(tab => (
              <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile Settings</CardTitle>
                <CardDescription>
                  Manage your profile information and preferences
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TimePreferencesSettings />
              </CardContent>
            </Card>
          </TabsContent>

          {currentMode === 'hire' && (
            <TabsContent value="company">
              <CompanySettings />
            </TabsContent>
          )}

          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Notification Settings</CardTitle>
                <CardDescription>
                  Manage your notification preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Notification Types */}
                <div className="space-y-4">
                  <h3 className="font-medium">Notification Types</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Messages</Label>
                        <p className="text-sm text-muted-foreground">
                          Receive notifications about new messages
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.messages}
                        onCheckedChange={(checked) => saveNotificationPrefs({ messages: checked })}
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Connections</Label>
                        <p className="text-sm text-muted-foreground">
                          Notifications about connection requests and updates
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.connections}
                        onCheckedChange={(checked) => saveNotificationPrefs({ connections: checked })}
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Job Opportunities</Label>
                        <p className="text-sm text-muted-foreground">
                          Notifications about new job matches and opportunities
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.opportunities}
                        onCheckedChange={(checked) => saveNotificationPrefs({ opportunities: checked })}
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Applications</Label>
                        <p className="text-sm text-muted-foreground">
                          Updates about your job applications
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.applications}
                        onCheckedChange={(checked) => saveNotificationPrefs({ applications: checked })}
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Interviews</Label>
                        <p className="text-sm text-muted-foreground">
                          Interview schedules and updates
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.interviews}
                        onCheckedChange={(checked) => saveNotificationPrefs({ interviews: checked })}
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Profile Views</Label>
                        <p className="text-sm text-muted-foreground">
                          When someone views your profile
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.profile_views}
                        onCheckedChange={(checked) => saveNotificationPrefs({ profile_views: checked })}
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Content Engagement</Label>
                        <p className="text-sm text-muted-foreground">
                          Reactions and comments on your content
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.content_engagement}
                        onCheckedChange={(checked) => saveNotificationPrefs({ content_engagement: checked })}
                      />
                    </div>

                    {/* Recruiter-specific notifications */}
                    {currentMode === 'hire' && (
                      <>
                        <Separator />
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label>Company Updates</Label>
                            <p className="text-sm text-muted-foreground">
                              Updates about your company profile and team
                            </p>
                          </div>
                          <Switch
                            checked={notificationPrefs.company_updates}
                            onCheckedChange={(checked) => saveNotificationPrefs({ company_updates: checked })}
                          />
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label>Team Activity</Label>
                            <p className="text-sm text-muted-foreground">
                              Activity from your hiring team
                            </p>
                          </div>
                          <Switch
                            checked={notificationPrefs.team_activity}
                            onCheckedChange={(checked) => saveNotificationPrefs({ team_activity: checked })}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Email Preferences */}
                <div className="space-y-4">
                  <h3 className="font-medium">Email Preferences</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Product Updates</Label>
                        <p className="text-sm text-muted-foreground">
                          Receive updates about new features and improvements
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.product_updates}
                        onCheckedChange={(checked) => saveNotificationPrefs({ product_updates: checked })}
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Marketing Emails</Label>
                        <p className="text-sm text-muted-foreground">
                          Receive promotional emails and newsletters
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.marketing_emails}
                        onCheckedChange={(checked) => saveNotificationPrefs({ marketing_emails: checked })}
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Weekly Digest</Label>
                        <p className="text-sm text-muted-foreground">
                          Weekly summary of your activity and opportunities
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.digest_emails}
                        onCheckedChange={(checked) => saveNotificationPrefs({ digest_emails: checked })}
                      />
                    </div>
                  </div>
                </div>

                {/* Notification Channels */}
                <div className="space-y-4">
                  <h3 className="font-medium">Notification Channels</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Email Notifications</Label>
                        <p className="text-sm text-muted-foreground">
                          Receive notifications via email
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.email_notifications}
                        onCheckedChange={(checked) => saveNotificationPrefs({ email_notifications: checked })}
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Push Notifications</Label>
                        <p className="text-sm text-muted-foreground">
                          Receive browser push notifications
                        </p>
                      </div>
                      <Switch
                        checked={notificationPrefs.push_notifications}
                        onCheckedChange={(checked) => saveNotificationPrefs({ push_notifications: checked })}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security">
            <SecuritySettings />
          </TabsContent>

          <TabsContent value="billing">
            <Card>
              <CardHeader>
                <CardTitle>Billing Settings</CardTitle>
                <CardDescription>
                  Manage your subscription and billing settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Billing settings content */}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}